// Baselayer Results Explorer — the staff results surface for a completed
// background check. Header (entity name, verified badge, breadcrumb + copy-id,
// two RatingGauges with "See Why", File-PDF action) over a Tabs nav whose
// panels render the KYB result + add-on data, all parsed client-side from the
// relevant rawResponseJson (defensively; nothing throws on a missing field).

import { useMemo, useState } from "react";
import { Check, Copy, FolderUp } from "lucide-react";
import {
  Button,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tabs,
  type TabItem,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useBackgroundCheck } from "@/hooks/backgroundChecks/useBackgroundChecks";
import { useAddOns } from "@/hooks/backgroundChecks/useAddOns";
import { FilePdfModal } from "@/components/backgroundChecks/FilePdfModal";
import { RatingGauge } from "./RatingGauge";
import { parseKyb, findScore } from "./parse";
import { ResultsTab } from "./tabs/ResultsTab";
import { DetailsTab } from "./tabs/DetailsTab";
import { AddressesTab } from "./tabs/AddressesTab";
import { SosFilingsTab } from "./tabs/SosFilingsTab";
import { OfficersTab } from "./tabs/OfficersTab";
import { WatchlistsTab } from "./tabs/WatchlistsTab";
import { LiensTab } from "./tabs/LiensTab";
import { LitigationsTab } from "./tabs/LitigationsTab";
import { BankruptciesTab } from "./tabs/BankruptciesTab";
import { WebsiteTab } from "./tabs/WebsiteTab";
import { ReviewsSocialsTab } from "./tabs/ReviewsSocialsTab";
import { ExclusionListTab } from "./tabs/ExclusionListTab";

const TABS: TabItem[] = [
  { id: "results", label: "Results" },
  { id: "details", label: "Details" },
  { id: "addresses", label: "Addresses" },
  { id: "sos", label: "SOS Filings" },
  { id: "officers", label: "Officers" },
  { id: "watchlists", label: "Watchlists" },
  { id: "liens", label: "Liens" },
  { id: "litigations", label: "Litigations" },
  { id: "bankruptcies", label: "Bankruptcies" },
  { id: "website", label: "Website" },
  { id: "reviews", label: "Reviews & Socials" },
  { id: "exclusions", label: "Exclusion List" },
];

export function ResultsExplorer({ checkId }: { checkId: string }) {
  const auth = useAuth();
  const detailQuery = useBackgroundCheck(checkId);
  const addOnsQuery = useAddOns(checkId);
  const [activeTab, setActiveTab] = useState("results");
  const [fileModalOpen, setFileModalOpen] = useState(false);

  const detail = detailQuery.data;
  const kyb = useMemo(
    () => parseKyb(detail?.result?.rawResponseJson ?? null),
    [detail?.result?.rawResponseJson],
  );

  if (detailQuery.isLoading) {
    return (
      <div className="flex flex-col gap-space-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!detail) {
    return (
      <p className="text-small text-gray-mid m-0">
        The background check you requested was not found or you don&apos;t have
        permission to view it.
      </p>
    );
  }

  const { check } = detail;
  const entityName = kyb.name ?? check.subjectName;
  const kybScore = findScore(kyb.scores, "kyb");
  const riskScore = findScore(kyb.scores, "risk");
  const canFile =
    check.status === "Completed" && auth.hasPermission("BackgroundCheck.Read");
  const addOns = addOnsQuery.data;

  return (
    <>
      <div className="flex flex-col gap-space-5">
        <PageHeader
          breadcrumbs={[
            { label: "Background Checks", to: "/background-checks" },
            { label: entityName },
          ]}
          eyebrow="Results Explorer"
          title={
            <span className="flex items-center gap-space-3 flex-wrap">
              {entityName}
              {kyb.verified && <StatusBadge tone="ok">Verified</StatusBadge>}
            </span>
          }
          actions={
            canFile ? (
              <Button
                type="button"
                onClick={() => setFileModalOpen(true)}
                data-testid="bgc-file-pdf-button"
              >
                <FolderUp size={16} aria-hidden="true" /> File PDF
              </Button>
            ) : undefined
          }
        />

        <CopyId id={check.id} />

        <div className="flex flex-wrap gap-space-6">
          <GaugeBlock label="KYB" score={kybScore} />
          <GaugeBlock label="Risk" score={riskScore} />
        </div>

        <Tabs
          tabs={TABS}
          activeId={activeTab}
          onChange={setActiveTab}
          ariaLabel="Results sections"
        >
          {activeTab === "results" && <ResultsTab kyb={kyb} />}
          {activeTab === "details" && <DetailsTab kyb={kyb} />}
          {activeTab === "addresses" && <AddressesTab kyb={kyb} />}
          {activeTab === "sos" && <SosFilingsTab kyb={kyb} />}
          {activeTab === "officers" && <OfficersTab kyb={kyb} />}
          {activeTab === "watchlists" && <WatchlistsTab kyb={kyb} />}
          {activeTab === "liens" && (
            <LiensTab checkId={check.id} addOns={addOns} />
          )}
          {activeTab === "litigations" && (
            <LitigationsTab checkId={check.id} addOns={addOns} />
          )}
          {activeTab === "bankruptcies" && (
            <BankruptciesTab checkId={check.id} addOns={addOns} />
          )}
          {activeTab === "website" && (
            <WebsiteTab checkId={check.id} addOns={addOns} />
          )}
          {activeTab === "reviews" && (
            <ReviewsSocialsTab checkId={check.id} addOns={addOns} />
          )}
          {activeTab === "exclusions" && <ExclusionListTab kyb={kyb} />}
        </Tabs>
      </div>

      <FilePdfModal
        open={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        checkId={check.id}
        dealId={check.dealId}
      />
    </>
  );
}

function GaugeBlock({
  label,
  score,
}: {
  label: string;
  score: ReturnType<typeof findScore>;
}) {
  const [showWhy, setShowWhy] = useState(false);
  const hasReasons = (score?.reasons.length ?? 0) > 0;

  return (
    <div className="flex flex-col items-center gap-space-2">
      <RatingGauge label={label} rating={score?.rating ?? null} />
      {hasReasons && (
        <>
          <button
            type="button"
            onClick={() => setShowWhy((v) => !v)}
            className="text-caption text-gold-shadow hover:underline"
            aria-expanded={showWhy}
          >
            {showWhy ? "Hide" : "See Why"}
          </button>
          {showWhy && (
            <ul className="m-0 list-disc pl-space-4 text-caption text-gray-mid max-w-[220px]">
              {score!.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function CopyId({ id }: { id: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast({ message: "ID copied.", tone: "success" });
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ message: "Could not copy ID.", tone: "error" });
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex w-fit items-center gap-space-1 text-caption text-gray-mid hover:text-onyx"
      title="Copy check ID"
    >
      {copied ? (
        <Check size={12} aria-hidden />
      ) : (
        <Copy size={12} aria-hidden />
      )}
      <span className="font-mono">{id}</span>
    </button>
  );
}
